import { type FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client';
import styles from './ChatPage.module.css';

export default function ChatPage() {
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [activeUser, setActiveUser] = useState<any>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [allStaff, setAllStaff] = useState<any[]>([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('auth-token');
      if (!token) return '';
      return JSON.parse(atob(token.split('.')[1])).userId || '';
    } catch {
      return '';
    }
  })();

  const fetchConversations = async () => {
    setLoadingConv(true);
    try {
      const data = await api.get<any[]>('/api/communication/chat/conversations');
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
    setLoadingConv(false);
  };

  const fetchMessages = async (userId: string) => {
    try {
      const data = await api.get<any[]>(`/api/communication/chat?with=${userId}`);
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (activeUser) fetchMessages(activeUser.id);
  }, [activeUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = (conv: any) => {
    setActiveUser(conv.user);
  };

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeUser) return;
    setSending(true);
    try {
      await api.post('/api/communication/chat', {
        content: newMessage.trim(),
        receiverId: activeUser.id,
      });
      setNewMessage('');
      fetchMessages(activeUser.id);
      fetchConversations();
    } catch {
      /* ignore */
    }
    setSending(false);
  };

  const openNewChat = async () => {
    try {
      const data = await api.get<any[]>('/api/misc/staff');
      setAllStaff(Array.isArray(data) ? data.filter((s: any) => s.id !== currentUserId) : []);
    } catch {
      /* ignore */
    }
    setShowNewChat(true);
  };

  const startNewChat = (user: any) => {
    setActiveUser(user);
    setShowNewChat(false);
    fetchMessages(user.id);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return formatTime(dateStr);
    return d.toLocaleDateString();
  };

  const roleLabel = (role: string) => {
    const labels: Record<string, string> = {
      SCHOOL_ADMIN: 'Admin',
      TEACHER: 'Docente',
      STAFF: 'Personal',
      PARENT: 'Padre',
    };
    return labels[role] || role;
  };

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Conversaciones</h2>
          <button className={styles.newChatBtn} onClick={openNewChat}>
            +
          </button>
        </div>

        <div className={styles.conversationList}>
          {loadingConv ? (
            <div className={styles.loadingText}>Cargando...</div>
          ) : conversations.length === 0 ? (
            <div className={styles.loadingText}>Sin conversaciones</div>
          ) : (
            conversations.map((conv, i) => (
              <div
                key={conv.user?.id || i}
                className={`${styles.conversation} ${activeUser?.id === conv.user?.id ? styles.activeConv : ''}`}
                onClick={() => selectConversation(conv)}
              >
                <div className={styles.convAvatar}>
                  {(conv.user?.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className={styles.convInfo}>
                  <div className={styles.convName}>{conv.user?.name || 'Usuario'}</div>
                  <div className={styles.convRole}>{roleLabel(conv.user?.role)}</div>
                  {conv.lastMessage && (
                    <div className={styles.convPreview}>{conv.lastMessage.content}</div>
                  )}
                </div>
                {conv.lastMessage && (
                  <div className={styles.convTime}>{formatDate(conv.lastMessage.createdAt)}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.main}>
        {!activeUser ? (
          <div className={styles.noChat}>
            <div className={styles.noChatIcon}>💬</div>
            <p>Selecciona una conversación o inicia una nueva</p>
            <button className={styles.startChatBtn} onClick={openNewChat}>
              Iniciar Conversación
            </button>
          </div>
        ) : (
          <>
            <div className={styles.chatHeader}>
              <div className={styles.chatAvatar}>
                {(activeUser.name || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className={styles.chatName}>{activeUser.name}</div>
                <div className={styles.chatRole}>{roleLabel(activeUser.role)}</div>
              </div>
            </div>

            <div className={styles.messages}>
              {messages.map((msg, i) => {
                const isMine = msg.senderId === currentUserId;
                const showAvatar = i === 0 || messages[i - 1]?.senderId !== msg.senderId;
                return (
                  <div
                    key={msg.id}
                    className={`${styles.message} ${isMine ? styles.mine : styles.theirs}`}
                  >
                    {!isMine && showAvatar && (
                      <div className={styles.msgAvatar}>
                        {(activeUser.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className={styles.msgBubble}>
                        <div className={styles.msgText}>{msg.content}</div>
                      </div>
                      <div className={styles.msgTime}>{formatTime(msg.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form className={styles.inputBar} onSubmit={handleSend}>
              <input
                className={styles.input}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe un mensaje..."
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className={styles.sendBtn}
              >
                {sending ? '...' : 'Enviar'}
              </button>
            </form>
          </>
        )}
      </div>

      {showNewChat && (
        <div className={styles.overlay}>
          <div className={styles.newChatModal}>
            <div className={styles.modalHeader}>
              <h3>Nueva Conversación</h3>
              <button onClick={() => setShowNewChat(false)} className={styles.closeBtn}>
                &times;
              </button>
            </div>
            <div className={styles.userList}>
              {allStaff.map((u) => (
                <div key={u.id} className={styles.userItem} onClick={() => startNewChat(u)}>
                  <div className={styles.userAvatar}>{(u.name || '?').charAt(0).toUpperCase()}</div>
                  <div>
                    <div className={styles.userName}>{u.name}</div>
                    <div className={styles.userRole}>{roleLabel(u.role)}</div>
                  </div>
                </div>
              ))}
              {allStaff.length === 0 && (
                <div className={styles.loadingText}>No hay usuarios disponibles</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
